import React, { useMemo } from "react";
import { Avatar, Box, Grid, Tooltip } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ChatIcon from "@mui/icons-material/Chat";
import PersonIcon from "@mui/icons-material/Person";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import {
  StatCard,
  CardHeading,
  CardSecondaryText,
  cardStatIconSx,
  formatNumber,
} from "./MediaKitComponents";
import { MediaKitAnalyticsLayout } from "./MediaKitAnalyticsLayout";
import { getS3Url } from "../../utils/imageUtils";
import type { PodcastMediaKit as PodcastMediaKitType } from "../../api/podcasts";
import { useTranslation } from "react-i18next";
import {
  MediaKitPodscopeCatalogConsumptionGrid,
  MediaKitConsumptionSubsection,
  catalogEngagementPercentFromConsumption,
} from "./MediaKitCatalogConsumptionCards";

type PaidBrandItem = NonNullable<
  PodcastMediaKitType["paidBrandsByProgram"]
>[number]["brands"][number];

interface MediaKitPodscopeAnalyticsSectionProps {
  analytics: PodcastMediaKitType["analytics"];
  paidBrandsByProgram?: PodcastMediaKitType["paidBrandsByProgram"];
  longFormContentCount?: number;
  streamingContentCount?: number;
}

const StreamingMetricCard = ({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
}) => (
  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
    <StatCard>
      {icon}
      <CardHeading variant="h5">{formatNumber(value)}</CardHeading>
      <CardSecondaryText variant="body2">{label}</CardSecondaryText>
    </StatCard>
  </Grid>
);

const iconSx = cardStatIconSx(30);

type PodscopeStreamingMetrics = NonNullable<
  NonNullable<PodcastMediaKitType["analytics"]>["podscope"]
>["streaming"];

const StreamingMetricsGrid = ({
  streaming,
  contentCount,
}: {
  streaming: NonNullable<PodscopeStreamingMetrics>;
  contentCount?: number;
}) => {
  const { t } = useTranslation("mediaKit");
  return (
  <Grid container spacing={2}>
    {contentCount != null && (
      <StreamingMetricCard
        icon={<LiveTvIcon sx={iconSx} />}
        value={contentCount}
        label="Livestreams"
      />
    )}
    {streaming.avgViewers != null && (
      <StreamingMetricCard
        icon={<GroupsIcon sx={iconSx} />}
        value={streaming.avgViewers}
        label={t("metrics.avgViewers")}
      />
    )}
    {streaming.peakViewers != null && (
      <StreamingMetricCard
        icon={<TrendingUpIcon sx={iconSx} />}
        value={streaming.peakViewers}
        label={t("metrics.peakViewers")}
      />
    )}
    {streaming.chatMessages != null && (
      <StreamingMetricCard
        icon={<ChatIcon sx={iconSx} />}
        value={streaming.chatMessages}
        label={t("metrics.chatMessages")}
      />
    )}
    {streaming.chatMessagesAvg != null && (
      <StreamingMetricCard
        icon={<ChatIcon sx={iconSx} />}
        value={streaming.chatMessagesAvg}
        label={t("metrics.chatMessagesAvg")}
      />
    )}
    {streaming.uniqueChatUsersAvg != null && (
      <StreamingMetricCard
        icon={<PersonIcon sx={iconSx} />}
        value={streaming.uniqueChatUsersAvg}
        label={t("metrics.uniqueChatUsersAvg")}
      />
    )}
  </Grid>
  );
};

export const MediaKitPodscopeAnalyticsSection: React.FC<
  MediaKitPodscopeAnalyticsSectionProps
> = ({
  analytics,
  paidBrandsByProgram,
  longFormContentCount,
  streamingContentCount,
}) => {
  const { t } = useTranslation("mediaKit");
  const ps = analytics?.podscope;
  const longFormConsumption = ps?.longForm?.consumption ?? ps?.consumption;
  const shortsConsumption = ps?.shorts?.consumption;
  const streaming = ps?.streaming;

  /** Single list for the section (no per-program subtitles); deduped across backend groups. */
  const allPaidBrandsDisplay = useMemo((): PaidBrandItem[] => {
    if (!paidBrandsByProgram?.length) return [];
    const byId = new Map<string, PaidBrandItem>();
    for (const g of paidBrandsByProgram) {
      for (const b of g.brands) {
        if (!byId.has(b._id)) byId.set(b._id, b);
      }
    }
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "es"),
    );
  }, [paidBrandsByProgram]);

  const hasLongFormConsumption =
    longFormConsumption != null &&
    Object.values(longFormConsumption).some((v) => v != null);
  const hasShortsConsumption =
    shortsConsumption != null &&
    Object.values(shortsConsumption).some((v) => v != null);
  const hasStreaming =
    streaming != null && Object.values(streaming).some((v) => v != null);
  const hasBrands = allPaidBrandsDisplay.length > 0;

  const longFormEngagementPct =
    catalogEngagementPercentFromConsumption(longFormConsumption);
  const shortsEngagementPct =
    catalogEngagementPercentFromConsumption(shortsConsumption);

  if (
    !hasLongFormConsumption &&
    !hasShortsConsumption &&
    !hasStreaming &&
    !hasBrands &&
    longFormContentCount == null &&
    streamingContentCount == null
  ) {
    return null;
  }

  return (
    <MediaKitAnalyticsLayout>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {(hasLongFormConsumption || longFormContentCount != null) && (
          <MediaKitConsumptionSubsection
            title="YouTube Videos"
            metricsWindow={{
              format: "longForm",
              inProgressCount: ps?.longForm?.consumption?.inProgressCount,
            }}
          >
            <MediaKitPodscopeCatalogConsumptionGrid
              consumption={longFormConsumption}
              engagementRate={
                longFormEngagementPct != null
                  ? longFormEngagementPct
                  : undefined
              }
              contentCount={longFormContentCount}
            />
          </MediaKitConsumptionSubsection>
        )}
        {hasShortsConsumption && (
          <MediaKitConsumptionSubsection
            title="YouTube Shorts"
            metricsWindow={{
              format: "shorts",
              inProgressCount: shortsConsumption?.inProgressCount,
            }}
          >
            <MediaKitPodscopeCatalogConsumptionGrid
              consumption={shortsConsumption}
              engagementRate={
                shortsEngagementPct != null ? shortsEngagementPct : undefined
              }
            />
          </MediaKitConsumptionSubsection>
        )}
        {(hasStreaming || streamingContentCount != null) && (
          <MediaKitConsumptionSubsection title="Streaming">
            <StreamingMetricsGrid
              streaming={streaming ?? {}}
              contentCount={streamingContentCount}
            />
          </MediaKitConsumptionSubsection>
        )}
        {hasBrands && (
          <MediaKitConsumptionSubsection title={t("supportedBy")}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "center",
              }}
            >
              {allPaidBrandsDisplay.map((brand) => (
                <Tooltip key={brand._id} title={brand.name} arrow>
                  <Avatar
                    src={getS3Url(brand.s3Key) || brand.logoUrl || undefined}
                    alt={brand.name}
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: "neutral.100",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {brand.name.charAt(0)}
                  </Avatar>
                </Tooltip>
              ))}
            </Box>
          </MediaKitConsumptionSubsection>
        )}
      </Box>
    </MediaKitAnalyticsLayout>
  );
};
